import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill } from "@/components/perf";
import { Crown, Trophy, Target, Zap, Award, Lock, Unlock, BookOpen, Rocket } from "lucide-react";
import PageBackdrop from "@/components/PageBackdrop";
import StartLeagueForm from "@/components/StartLeagueForm";
import UpcomingLeagues from "@/components/UpcomingLeagues";
import MyLeagues from "@/components/MyLeagues";
import PartnerInvites from "@/components/PartnerInvites";

const DIVS = ["Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Pro"];

export default function League() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [info, setInfo] = useState(null);
  const [division, setDivision] = useState("Intermediate");
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showStart, setShowStart] = useState(false);
  const [leaguesKey, setLeaguesKey] = useState(0);
  const isAdmin = user?.role === "admin";
  const canDirect = isAdmin || user?.is_td || user?.is_coach;

  useEffect(() => {
    (async () => {
      const [s, i] = await Promise.all([api.get("/bpl/status"), api.get("/bpl/league-info")]);
      setStatus(s.data); setInfo(i.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const [st, m] = await Promise.all([
        api.get("/bpl/standings", { params: { division } }),
        api.get("/bpl/matches", { params: { division } }),
      ]);
      setStandings(st.data); setMatches(m.data);
    })();
  }, [division]);

  useAutoRefresh(async () => {
    const [s, st, m] = await Promise.all([
      api.get("/bpl/status"),
      api.get("/bpl/standings", { params: { division } }),
      api.get("/bpl/matches", { params: { division } }),
    ]);
    setStatus(s.data); setStandings(st.data); setMatches(m.data);
  });

  const isVip = status?.is_vip;

  return (
    <div>
      <PageBackdrop ck="backdrop.league" image="https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/87900b68784fc99280a9f69e7b1003f1e0733b2378b3df0b639b50aa7284b720.png" />
      <div className="relative z-10">
      <PartnerInvites />
      <div className="flex items-center gap-4 flex-wrap">
        <img src="https://customer-assets.emergentagent.com/job_pickle-os/artifacts/cj28ut5a_AFEA36B2-2E75-4ED3-9048-93AFF9BF75C1.png"
          alt="BPL League" data-testid="bpl-header-logo" className="w-20 h-20 sm:w-24 sm:h-24 object-contain shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <PageHeader ck="page.league" kicker="Competitive Layer · Bullpen Performance League"
            title="BPL League"
            subtitle="Three sequential phases per match — Dink Wars, Transition Wars, Game Play. Team Points measure skill; Match Wins measure results; sweep all three for a Bonus." />
        </div>
        <button data-testid="league-overview-btn" onClick={() => navigate("/league/overview")}
          className="shrink-0 flex items-center gap-2 border border-pb-cyan/50 text-pb-cyan hover:bg-pb-cyan hover:text-black font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors">
          <BookOpen className="w-4 h-4" /> League Overview
        </button>
        {canDirect && (
          <button data-testid="start-league-btn" onClick={() => setShowStart(true)}
            className="shrink-0 flex items-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest text-xs px-4 py-2 hover:opacity-90 active:scale-95 transition-all">
            <Rocket className="w-4 h-4" /> Start a League
          </button>
        )}
      </div>

      {/* VIP banner */}
      <div className={`border p-4 mb-6 flex items-center gap-3 flex-wrap ${isVip ? "border-pb-cyan/40 bg-pb-cyan/5" : "border-pb-red/40 bg-pb-red/5"}`} data-testid="bpl-gate">
        {isVip ? <Unlock className="w-5 h-5 text-pb-cyan shrink-0" /> : <Lock className="w-5 h-5 text-pb-red shrink-0" />}
        <div className="flex-1 min-w-[200px]">
          <div className="font-display font-bold uppercase">{isVip ? "You're In The League" : "League Entry — VIP / ACE Only"}</div>
          <div className="text-xs text-zinc-400">{isVip ? "Membership active — you can be rostered to compete." : "Standings are public. Competing requires a VIP ($1/mo) or ACE ($8/mo) membership."}</div>
        </div>
        {isVip ? (
          <button data-testid="roster-league-btn" onClick={() => navigate("/league/register")}
            className="flex items-center gap-2 bg-pb-cyan text-black font-bold uppercase tracking-widest px-4 py-2 hover:bg-[#86EFAC] active:scale-95 transition-all text-sm whitespace-nowrap">
            <Trophy className="w-4 h-4" /> Roster for the League
          </button>
        ) : (
          <button data-testid="upgrade-vip-btn" onClick={() => navigate(`/upgrade?reason=${encodeURIComponent("Competing in the BPL League requires a VIP or ACE membership.")}`)}
            className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest px-4 py-2 hover:bg-[#BEF264] active:scale-95 transition-all text-sm whitespace-nowrap">
            <Crown className="w-4 h-4" /> Upgrade
          </button>
        )}
      </div>

      {/* Director dashboard — my leagues */}
      {canDirect && <MyLeagues refreshKey={leaguesKey} />}

      {/* Upcoming custom leagues */}
      <UpcomingLeagues refreshKey={leaguesKey} />

      {/* Phase rules */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {info?.phases.map((p) => (
          <div key={p.order} className="bg-pb-surface border border-pb-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-display font-black text-2xl text-zinc-700">{p.order}</span>
              <span className="font-display font-bold uppercase">{p.name}</span>
            </div>
            <Pill color={p.type === "Match Result" ? "#A3E635" : "#4ADE80"}>{p.type}</Pill>
            <div className="text-xs text-zinc-500 mt-2">{p.reward}</div>
          </div>
        ))}
      </div>

      {/* Division filter */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-zinc-500">Division:</span>
        {DIVS.map((d) => (
          <button key={d} data-testid={`div-${d}`} onClick={() => setDivision(d)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${division === d ? "bg-pb-red text-black border-pb-red" : "border-pb-border text-zinc-500 hover:text-white"}`}>
            {d}
          </button>
        ))}
      </div>

      {/* Standings */}
      <div className="bg-pb-surface border border-pb-border mb-8" data-testid="standings">
        <div className="flex items-center gap-2 p-5 border-b border-pb-border">
          <Trophy className="w-5 h-5 text-pb-red" />
          <h3 className="font-display font-bold text-xl uppercase">{division} Standings</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-pb-border/60">
                <th className="text-left px-5 py-3">#</th>
                <th className="text-left px-2 py-3">Team</th>
                <th className="text-center px-2 py-3"><span className="text-pb-cyan">Match</span> W</th>
                <th className="text-center px-2 py-3">L</th>
                <th className="text-center px-2 py-3"><span className="text-pb-red">Team</span> Pts</th>
                <th className="text-center px-3 py-3">Bonus</th>
              </tr>
            </thead>
            <tbody>
              {standings.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-zinc-600">No standings yet for this division.</td></tr>}
              {standings.map((r) => (
                <tr key={r.team_id} className="border-b border-pb-border/40">
                  <td className="px-5 py-3 font-display font-black text-zinc-500">{r.rank}</td>
                  <td className="px-2 py-3 font-semibold">{r.name}</td>
                  <td className="px-2 py-3 text-center font-display font-black text-pb-cyan text-lg">{r.wins}</td>
                  <td className="px-2 py-3 text-center font-mono text-zinc-400">{r.losses}</td>
                  <td className="px-2 py-3 text-center font-display font-black text-pb-red text-lg">{r.team_points}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="inline-flex items-center gap-1 font-mono text-amber-400">
                      {r.bonus_points > 0 && <Award className="w-3.5 h-3.5" />}{r.bonus_points}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-4 px-5 py-3 border-t border-pb-border/60 text-[11px] text-zinc-500">
          <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-pb-cyan" /> Match Wins = Game Play results</span>
          <span className="flex items-center gap-1"><Target className="w-3 h-3 text-pb-red" /> Team Points = Dink + Transition Wars (skill)</span>
          <span className="flex items-center gap-1"><Award className="w-3 h-3 text-amber-400" /> Bonus = swept all 3 phases</span>
        </div>
      </div>

      {/* Recent matches */}
      <div className="bg-pb-surface border border-pb-border" data-testid="match-log">
        <div className="p-5 border-b border-pb-border"><h3 className="font-display font-bold text-xl uppercase">Match Results</h3></div>
        <div className="divide-y divide-pb-border/40">
          {matches.length === 0 && <div className="px-5 py-6 text-sm text-zinc-600">No matches scheduled.</div>}
          {matches.map((m) => (
            <div key={m.id} className="px-5 py-3 flex items-center gap-3 text-sm">
              <span className="text-xs text-zinc-500 font-mono w-20 shrink-0">{m.date}</span>
              <span className="flex-1 font-semibold">
                {m.team_a_name} <span className="text-zinc-600">vs</span> {m.team_b_name}
              </span>
              {m.status === "Locked" && m.scoring ? (
                <>
                  <span className="font-mono text-zinc-400 hidden sm:inline">
                    {m.scoring.team_a_points}-{m.scoring.team_b_points} pts
                  </span>
                  {m.scoring.bonus_awarded && <Award className="w-4 h-4 text-amber-400" />}
                  <span className="font-display font-bold text-pb-cyan">
                    {m.scoring.match_winner === m.team_a ? m.team_a_name : m.team_b_name} W
                  </span>
                </>
              ) : (
                <Pill color="#71717A">Pending</Pill>
              )}
            </div>
          ))}
        </div>
      </div>
      </div>
      {showStart && <StartLeagueForm onClose={() => setShowStart(false)} onCreated={() => setLeaguesKey((k) => k + 1)} />}
    </div>
  );
}
